import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Users, Send, ShieldOff } from "lucide-react";

export default function FamilyShareCard({ contactEmail }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/family/links").then((r) => setStatus(r.data));
  useEffect(() => { load(); }, []);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/family/links");
      toast.success(`Family View link sent to ${data.sent_to}`);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true);
    try { await api.delete("/family/links"); toast.success("Family sharing turned off"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const shared = status && (status.active_sessions > 0 || status.pending_link);
  return (
    <section data-testid="family-share-card" className="bg-white border border-[#E2D9CE] rounded-2xl p-6 sm:p-8 shadow-sm mt-8">
      <div className="flex items-center gap-3 mb-2"><Users className="w-7 h-7 text-[#2E7D6B]" /><h2 className="text-2xl font-display font-bold text-slate-900">Family View</h2></div>
      <p className="text-slate-600 mb-5">Let your family contact see your upcoming visits and safety check-ins — read only, no password needed.</p>
      {!contactEmail ? (
        <p data-testid="family-share-no-email" className="bg-[#FFF7E6] text-[#A9741A] rounded-xl p-4 font-medium">Add your family contact's email above and save to enable sharing.</p>
      ) : (
        <>
          <p data-testid="family-share-status" className="text-slate-700 mb-4">
            {status?.active_sessions > 0 ? `Sharing is on — ${status.last_sent_to} has opened your Family View.`
              : status?.pending_link ? `A link was sent to ${status.last_sent_to} and hasn't been opened yet.`
              : `Not shared yet. We'll email a private link to ${contactEmail}.`}
          </p>
          <div className="flex flex-wrap gap-3">
            <button data-testid="family-share-send-btn" onClick={send} disabled={busy}
              className="flex items-center gap-2 bg-[#2E7D6B] hover:bg-[#256658] disabled:opacity-60 text-white font-semibold px-5 min-h-[52px] rounded-xl transition-colors">
              <Send className="w-5 h-5" /> {shared ? "Send link again" : "Share with my family contact"}
            </button>
            {shared && (
              <button data-testid="family-share-revoke-btn" onClick={revoke} disabled={busy}
                className="flex items-center gap-2 border-2 border-[#E2D9CE] hover:border-red-400 text-red-700 font-semibold px-5 min-h-[52px] rounded-xl transition-colors">
                <ShieldOff className="w-5 h-5" /> Turn off sharing
              </button>
            )}
          </div>
          {status?.alert_prefs && (
            <div data-testid="family-alert-prefs-readonly" className="mt-5 pt-4 border-t border-[#E2D9CE]">
              <p className="font-semibold text-slate-800 mb-2">Alerts your contact has chosen to receive</p>
              <div className="flex flex-wrap gap-2">
                {[["bookings", "Bookings"], ["completions", "Completions"], ["safety", "Safety alerts"]].map(([k, l]) => (
                  <span key={k} data-testid={`alert-pref-readonly-${k}`} className={`px-3 py-1.5 rounded-full text-sm font-semibold ${status.alert_prefs[k] ? "bg-[#E8F4F1] text-[#1F5F4F]" : "bg-slate-100 text-slate-500 line-through"}`}>{l}</span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
